//
// the overall userType is also stored on the token
// as either clinician or trialStaff
//

export type COUNTRY_TYPE = {
  abbreviation: string
  countryCode: string
  name: string
}

export enum QUESTIONNAIRE_TYPE_ENUM {
  //DRAFT = 'DRAFT',
  //VISIT = 'VISIT',
  PHASE = 'PHASE',
  GROUP = 'GROUP',
  ONBOARDING = 'ONBOARDING',
  ELIGIBILITY = 'ELIGIBILITY',
  STANDARD = 'STANDARD',
  TRIGGERED = 'TRIGGERED',
}

export const TOKEN_USER_TYPES = {
  CLINICIAN: 'CLINICIAN',
  TRIAL_STAFF: 'TRIAL_STAFF',
} as const

export type TokenUserTypes = keyof typeof TOKEN_USER_TYPES

export enum PROJECT_STATES {
  DRAFT = 'DRAFT',
  ACTIVE = 'ACTIVE',
  RETIRED = 'RETIRED',
  ARCHIVED = 'ARCHIVED',
}

export enum RESOURCE_STATUS {
  DRAFT = 'DRAFT',
  ACTIVE = 'ACTIVE',
  RETIRED = 'RETIRED',
}

//
// These user types are stored on the user document
//
export const USER_TYPES = {
  CTM: 'CTM',
  CRA: 'CRA',
  DM: 'DM',
  CLINICIAN: 'CLINICIAN',
  TRIAL_STAFF: 'TRIAL_STAFF',
} as const

export type UserTypes = keyof typeof USER_TYPES

export enum TERMINATION_REASONS {
  DISCONTINUED = 'DISCONTINUED',
  SCREEN_FAILURE = 'SCREEN_FAILURE',
  OTHER = 'OTHER',
}

export enum SUBJECT_STATUS_ENUM {
  CREATED = 'CREATED',
  ACTIVE = 'ACTIVE',
  TERMINATED = 'TERMINATED',
  IN_STUDY = 'IN_STUDY', // should be removed
  DISQUALIFIED = 'DISQUALIFIED', // should be removed
  COMPLETED = 'COMPLETED',
}

export enum TRIAL_STAFF_TYPE_ENUM {
  CTM = 'CTM',
  CRA = 'CRA',
  DM = 'DM',
}

export enum CRUD_VIEW_TYPE {
  CREATE = 'CREATE',
  UPDATE = 'UPDATE',
  VIEW = 'VIEW',
}

export type SubjectType = {
  _id: string
  displayId: string
  screeningId: string
  createdBy: string
  deviceId: string
  onboard: string
  currentPhase: { title: string; duration: number; _id: string }
  projectId: string
  siteId: { siteNumber: string; title: string }
  createdAt: Date
  status: SUBJECT_STATUS_ENUM
  language: string
  bodyPart: string
}

export const FLOW_TYPES = {
  ORDERED: 'ORDERED',
  DEPENDENCY: 'DEPENDENCY',
} as const

export type FlowType = (typeof FLOW_TYPES)[keyof typeof FLOW_TYPES]

export type AssignmentType = {
  _id: string
  answers: any[]
  createdAt: string
  endDate: string
  name: string
  phaseId: string
  progress: number
  projectId: string
  questionnaireId: string
  questions: any[]
  recurrence: string
  recurrenceType: string
  startDate: string
  flowType: FlowType
  sub: string
  subjectId: string
  isOnboarding: boolean
}

export type Answer = {
  _id: string
  questionnaireId: string
  questionId: string
  assignmentId: string
  value: string
  language: string
  subjectId: string
  deviceTime: string
  offline: boolean
  createdAt: string
  updatedAt: string
}

export type Questionnaire = {
  _id: string
  belongsTo: string
  belongsToModel: string
  createdAt: string
  eligibility: boolean
  end: string
  internalTitle: string
  name: {
    en: string
    [key: string]: string
  }
  notifications: {
    reminders: Record<string, any>
  }
  onboarding: boolean
  projectId: string
  questions: string[]
  recurrence: {
    endAt: { h: Number; m: Number }
    startAt: { h: Number; m: Number }
    type: string
  }
  start: string
  status: RESOURCE_STATUS
  type: QUESTIONNAIRE_TYPE_ENUM
  updatedAt: string
}

export type Phase = {
  _id: string
  active: false
  bodyParts: string[]
  createdAt: Date
  duration: number
  groups: any[]
  order: 1
  originalId: string
  projectId: string
  questionnaires: Questionnaire[]
  stableId: string
  status: RESOURCE_STATUS
  title: string
  type: 'IN_SCREENING' | 'IN_STUDY'
  updatedAt: Date
}

export type SiteType = {
  _id: string
  projectId: string
  siteNumber: number
  title: string
  address: string
  phone: { tel: number; countryCode: string }
  contactPersonName: string
  contactPersonJobTitle: string
  contactPersonEmail: string
  active: boolean
  createdAt: Date
  timeZone: string
  updatedAt: Date
}

type Language = {
  name: string
  isoCode: string
  active: boolean
}

type Phone = {
  tel: number
  countryCode: string
}

type Country = {
  name: string
  abbreviation: string
  code: string
}

type ActiveProtocol = {
  _id: string
  projectId: string
  originalId: string
  title: string
  status: string
  phases: string[]
  visits: any[]
  questionnaires: string[]
  createdAt: string
  updatedAt: string
  __v: number
  activatedAt: string
}

export type Projects = {
  _id: string
  status: string
  title: string
  displayName: string
  studyId: string
  sponsor: string
  cro: string
  training: number
  country: Country[]
  languages: Language[]
  condition: string
  createdAt: string
  updatedAt: string
  __v: number
  activeProtocol: ActiveProtocol | null
}

export type ClinicianWithProjects = {
  _id: string
  name: string
  email: string
  phone: Phone
  projectId: string[]
  type: string
  status: string
  sites: SiteType[]
  jobTitle: string
  createdBy: string
  sub: string
  createdAt: string
  updatedAt: string
  __v: number
  inviteSent: string
  training: number
  projects: Projects
}

// ---------------

export type Translation = {
  values: {
    language: string // ISO country code
    value: string // value of the string
  }[]
}

// See https://www.loc.gov/standards/iso639-2/php/English_list.php for specification, add as needed
// Not sure if something built in is smarter for this though...
export type LanguageISOCode = 'en' | 'es' | 'da'

export const QuestionsTypes = {
  radio: 'radio',
  checkbox: 'checkbox',
  text: 'text',
  number: 'number',
  info: 'info',
  bmi: 'bmi',
  phonenumber: 'phonenumber',
  name: 'name',
  gender: 'gender',
  birthdate: 'birthdate',
  nprs5: 'nprs5',
  nprs11pt5l: 'nprs11pt5l',
  nprs11pt2l: 'nprs11pt2l',
  yesno: 'yesno',
  unitcounter: 'unitcounter',
} as const

export type QuestionType = keyof typeof QuestionsTypes

export interface Question {
  _id: string
  questionnaireId: string
  title: Translation
  body: Translation
  continuation: Translation
  type: QuestionType
  lastModified: Date
  next: (string | null)[]
  storage: string
  required: boolean
  externalStorageId?: string
  options?: any[]
}

export const DEVICE_STATE = {
  ENROLL_NEW_DEVICE: 'ENROLL_NEW_DEVICE',
  CREATED: 'CREATED',
  ACTIVE: 'ACTIVE',
  DISABLE_DEVICE: 'DISABLE_DEVICE',
  ENABLE_DEVICE: 'ENABLE_DEVICE',
  WIPE_DEVICE: 'WIPE_DEVICE',
  WIPED: 'WIPED',
} as const

export type DEVICE_STATE_TYPE = keyof typeof DEVICE_STATE

export enum AUDIT_TYPES {
  TERMINATION = 'TERMINATION',
  REACTIVATION = 'REACTIVATION',
  MOVE = 'MOVE',
  MOVE_ELIGIBILITY = 'MOVE_ELIGIBILITY',
  MOVE_BACK = 'MOVE_BACK',
  VIEW_ELIGIBILITY = 'VIEW_ELIGIBILITY',
  ASSESSMENT_ELIGIBILITY = 'ASSESSMENT_ELIGIBILITY',
  COMPLETE = 'COMPLETE',
  UNCOMPLETE = 'UNCOMPLETE',
  CREATE_CLINICIAN = 'CREATE_CLINICIAN',
  CREATE_TRIALSTAFF = 'CREATE_TRIALSTAFF',
  CREATE_SUBJECT = 'CREATE_SUBJECT',
  UPDATE_SUBJECT = 'UPDATE_SUBJECT',
  REQUEST_SUBJECT_PII = 'REQUEST_SUBJECT_PII',
  UPDATE_TRIALSTAFF = 'UPDATE_TRIALSTAFF',
  UPDATE_TRIALSTAFF_SITES = 'UPDATE_TRIALSTAFF_SITES',
  UPDATE_TRIALSTAFF_PII = 'UPDATE_TRIALSTAFF_PII',
  UPDATE_CLINICIAN_PII = 'UPDATE_CLINICIAN_PII',
  UPDATE_CLINICIAN_SITE_ACCESS = 'UPDATE_CLINICIAN_SITE_ACCESS',
  UPDATE_CLINICIAN_PROJECT_ACCESS = 'UPDATE_CLINICIAN_PROJECT_ACCESS',
  PORTAL_LOGOUT = 'PORTAL_LOGOUT',

  INVITE = 'INVITE',
  REINVITE = 'REINVITE',

  ACCESS = 'ACCESS',

  EXPORT_SUBJECT_DATA = 'EXPORT_SUBJECT_DATA',
  EXPORT_SITES_DATA = 'EXPORT_SITES_DATA',
  EXPORT_SUBJECT_AUDITS = 'EXPORT_SUBJECT_AUDITS',
  EXPORT_SUBJECT_ELIGIBILITY = 'EXPORT_SUBJECT_ELIGIBILITY',
  EXPORT_SUBJECT_AUDITS_EXTENDED = 'EXPORT_SUBJECT_AUDITS_EXTENDED',
  EXPORT_LOGINS = 'EXPORT_LOGINS',
  EXPORT_DEPENDENCY_TREES = 'EXPORT_DEPENDENCY_TREES',

  LOGIN_SUCCESS_SUBJECT = 'LOGIN_SUCCESS_SUBJECT',
  LOGIN_FAILED_SUBJECT = 'LOGIN_FAILED_SUBJECT',

  SUBJECT_RESET_MISSING_EMAIL = 'SUBJECT_RESET_MISSING_EMAIL',
  SUBJECT_CHANGED_PIN = 'SUBJECT_CHANGED_PIN',
  SUBJECT_RESET_VERIFICATION_CODE_SENT = 'SUBJECT_RESET_VERIFICATION_CODE_SENT',
  SUBJECT_RESET_PIN_CONFIRM_CODE_SUCCESS = 'SUBJECT_RESET_PIN_CONFIRM_CODE_SUCCESS',
  SUBJECT_RESET_PIN_CONFIRM_CODE_FAILED = 'SUBJECT_RESET_PIN_CONFIRM_CODE_FAILED',
  SUBJECT_REPLACE_DEVICE = 'SUBJECT_REPLACE_DEVICE',
  SUBJECT_ACTIVATE_DEVICE = 'SUBJECT_ACTIVATE_DEVICE',

  SUBJECT_START_ASSIGNMENT = 'SUBJECT_START_ASSIGNMENT',
  SUBJECT_LOST_DEVICE = 'SUBJECT_LOST_DEVICE',

  DATA_EVALUATION_SUCCESS = 'DATA_EVALUATION_SUCCESS',

  TRAINING_COMPLETED = 'TRAINING_COMPLETED',

  CREATE_GROUP_ACTION = 'CREATE_GROUP_ACTION',

  DOWNLOAD_ELIGIBILITY_PDF = 'DOWNLOAD_ELIGIBILITY_PDF',
  UPDATE_DISPLAY_ID = 'UPDATE_DISPLAY_ID',
  EMAIL_NOTIFICATION_SEEN = 'EMAIL_NOTIFICATION_SEEN',
}
